import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Layout from "../../components/Layout";
import LoadingScreen from "../../components/LoadingScreen";
import apiClient from "../../services/apiClient";
import {
    ArrowLeft,
    Phone,
    MapPin,
    Tag,
    DollarSign,
    ListChecks,
    PawPrint,
    ReceiptIndianRupee,
    Calendar, Clock, CreditCard, Info, User, Star, MessageSquare, ThumbsUp
} from "lucide-react";
import { motion } from "framer-motion";


const ReviewCard = ({ review, loadingReview }: any) => {
    if (loadingReview) {
        return (
            <div className="bg-gradient-to-b from-purple-50 to-white p-6 rounded-2xl shadow-md animate-pulse">
                {/* Header shimmer */}
                <div className="flex items-center justify-between mb-6">
                    <div className="h-6 bg-gradient-to-r from-purple-200 via-purple-100 to-purple-200 rounded-full w-48 animate-shimmer"></div>
                    <div className="h-6 bg-gradient-to-r from-purple-200 via-purple-100 to-purple-200 rounded-full w-32 animate-shimmer"></div>
                </div>

                {/* Comment shimmer */}
                <div className="h-24 bg-gradient-to-r from-purple-200 via-purple-100 to-purple-200 rounded-2xl mb-6 animate-shimmer"></div>

                {/* Cards shimmer */}
                <div className="grid grid-cols-2 gap-4">
                    <div className="h-16 bg-gradient-to-r from-purple-200 via-purple-100 to-purple-200 rounded-xl animate-shimmer"></div>
                    <div className="h-16 bg-gradient-to-r from-purple-200 via-purple-100 to-purple-200 rounded-xl animate-shimmer"></div>
                </div>
            </div>
        );
    }


    if (!review) {
        return (
            <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-3xl p-12 shadow-lg border-2 border-slate-200 text-center relative overflow-hidden">
                <div className="absolute inset-0 bg-grid-slate-200/50 [mask-image:linear-gradient(0deg,white,rgba(255,255,255,0.6))]" style={{ backgroundSize: '20px 20px', backgroundImage: 'linear-gradient(to right, #e2e8f0 1px, transparent 1px), linear-gradient(to bottom, #e2e8f0 1px, transparent 1px)' }}></div>
                <div className="relative">
                    <div className="inline-flex p-4 bg-slate-200 rounded-full mb-4">
                        <MessageSquare className="w-12 h-12 text-slate-400" />
                    </div>
                    <p className="text-slate-600 font-semibold text-lg">No review submitted yet</p>
                    <p className="text-slate-500 text-sm mt-2">The client hasn't provided feedback for this Service</p>
                </div>
            </div>
        );
    }

    const rating = review.rating || 0;

    return (
        <div className="bg-white ">
            <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-purple-300/20 to-pink-300/20 rounded-full blur-3xl -mr-32 -mt-32 group-hover:scale-150 transition-transform duration-700" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-gradient-to-tr from-indigo-300/20 to-blue-300/20 rounded-full blur-2xl -ml-24 -mb-24 group-hover:scale-125 transition-transform duration-700" />

            <div className="relative">
                <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
                    <div className="flex items-center gap-4">
                        <div className="p-4 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl shadow-lg group-hover:rotate-12 transition-transform duration-300">
                            <Star className="w-7 h-7 text-white" />
                        </div>
                        <div>
                            <h3 className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                                Client Review
                            </h3>
                            <p className="text-sm text-slate-600 mt-1">Feedback & Rating</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 bg-white px-6 py-3 rounded-2xl">
                        <div className="flex items-center gap-1">
                            {[...Array(5)].map((_, i) => (
                                <Star
                                    key={i}
                                    className={`w-6 h-6 transition-all duration-300 ${i < rating
                                        ? 'text-amber-400 fill-amber-400 drop-shadow-[0_2px_4px_rgba(251,191,36,0.5)]'
                                        : 'text-gray-300'
                                        }`}
                                />
                            ))}
                        </div>
                        <div className="ml-2 flex flex-col items-end">
                            <span className="text-2xl font-black bg-gradient-to-r from-amber-500 to-orange-500 bg-clip-text text-transparent">
                                {rating}
                            </span>
                            <span className="text-xs text-gray-500 font-medium">out of 5</span>
                        </div>
                    </div>
                </div>

                {review.comment && (
                    <div className="bg-white/90 backdrop-blur-md rounded-2xl p-6 mb-6 shadow-lg border border-purple-100 group-hover:border-purple-300 transition-colors duration-300">
                        <div className="flex flex-col gap-3">
                            <div className="flex gap-3">
                                <MessageSquare className="w-5 h-5 text-purple-500 mt-1 flex-shrink-0" />
                                <p className="text-gray-700 leading-relaxed text-base italic font-medium">
                                    "{review.comment}"
                                </p>
                            </div>

                            <div className="flex items-center gap-2 ml-8">
                                {/* <p className="text-xs font-bold text-purple-600 uppercase tracking-wider">Reviewed On</p> */}
                                <p className="text-sm font-semibold text-gray-900">
                                    {new Date(review.created_at).toLocaleDateString('en-US', {
                                        month: 'short',
                                        day: 'numeric',
                                        year: 'numeric',
                                    })}
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                    {review.reviewer_name && (
                        <div className="flex items-center gap-4 bg-white/70 backdrop-blur-sm rounded-xl px-5 py-4 shadow-md border border-purple-100 hover:bg-white transition-all duration-300 group/item">
                            <div className="p-2 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-lg group-hover/item:scale-110 transition-transform duration-300">
                                <User className="w-5 h-5 text-white" />
                            </div>
                            <div>
                                <p className="text-xs font-bold text-purple-600 uppercase tracking-wider">Reviewer</p>
                                <p className="text-sm font-semibold text-gray-900 mt-0.5">{review.reviewer_name}</p>
                            </div>
                        </div>
                    )}

                </div>

                {review.would_recommend !== undefined && (
                    <div className={`flex items-center gap-3 rounded-2xl px-5 py-4 border-2 shadow-lg transition-all duration-300 ${review.would_recommend
                        ? 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-300 hover:shadow-green-200'
                        : 'bg-gradient-to-r from-red-50 to-rose-50 border-red-300 hover:shadow-red-200'
                        }`}>
                        <div className={`p-2 rounded-lg ${review.would_recommend ? 'bg-green-500' : 'bg-red-500'}`}>
                            <ThumbsUp className={`w-5 h-5 text-white ${!review.would_recommend && 'rotate-180'}`} />
                        </div>
                        <p className={`text-sm font-bold ${review.would_recommend ? 'text-green-800' : 'text-red-800'}`}>
                            {review.would_recommend ? '✓ Would recommend this service' : '✗ Would not recommend'}
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};
const PetServiceOrderDetails = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [order, setOrder] = useState<any>(null);
    console.log("orde r", order?.id);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [isOpen, setIsOpen] = useState(false);
    const [review, setReview] = useState<any>(null);
    const [loadingReview, setLoadingReview] = useState(false);
    const addressDetails = order?.owner_address?.additional_info?.address_details || {};
    const formatAddress = (addr: any) => {
        const fields = [
            addr.door_no,
            addr.road,
            addr.landmark,
            addr.neighbourhood,
            addr.suburb,
            addr.city,
            addr.state_district,
            addr.state,
            addr.postcode,
            addr.country
        ];

        // Filter out empty values and join with comma
        return fields.filter(f => f && f.trim() !== "").join(", ") || "N/A";
    };
    useEffect(() => {
        const controller = new AbortController();

        const fetchReview = async () => {
            if (!order?.id) return;

            setLoadingReview(true);
            try {
                const { data } = await apiClient(
                    `v1/resource/ConsultReview?consult_id=${order?.id}`,
                    {
                        method: 'GET',
                    }
                ) as any;
                const matchingReview = Array.isArray(data?.consult_reviews)
                    ? data.consult_reviews.find((r: any) => r.additional_ids?.order_id === order?.id)
                    : data?.consult_review;
                setReview(matchingReview || null);
            } catch (error: any) {
                if (error.name !== 'AbortError') {
                    console.error('Error fetching review:', error);
                }
            } finally {
                setLoadingReview(false);
            }
        };

        fetchReview();

        return () => controller.abort();
    }, [order?.id]);



    const fetchOrderDetails = async () => {
        try {
            setLoading(true);
            const response: any = await apiClient(`v1/resource/ServiceOrder/${id}`, { method: "GET" });
            setOrder(response.data.service_orders);
        } catch (err: any) {
            setError(err.message || "Failed to load order details");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (id) fetchOrderDetails();
    }, [id]);

    if (loading) return <LoadingScreen message="Loading Order Details..." />;
    if (error)
        return (
            <Layout>
                <div className="text-center mt-10 text-red-600 font-semibold">{error}</div>
            </Layout>
        );
    if (!order)
        return (
            <Layout>
                <div className="text-center mt-10 text-gray-500">No order found.</div>
            </Layout>
        );

    const infoItem = (
        icon: React.ReactNode,
        label: string,
        value: React.ReactNode,
        highlight: boolean = false
    ) => (
        <motion.div
            className={`flex items-start gap-3 text-sm p-2 rounded-lg border ${highlight
                ? "border-blue-300 bg-blue-50"
                : "border-gray-100 bg-white"
                } hover:border-gray-200 hover:shadow-sm transition-all duration-200`}
            whileHover={{ scale: 1.01 }}
        >
            {/* Icon */}
            <div
                className={`flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-md ${highlight ? "bg-blue-100 text-blue-600" : "bg-gray-50 text-gray-500"
                    }`}
            >
                {icon}
            </div>

            {/* Text */}
            <div className="flex flex-col">
                <span
                    className={`text-xs uppercase tracking-wide font-medium ${highlight ? "text-blue-600" : "text-gray-500"
                        }`}
                >
                    {label}
                </span>
                <span
                    className={`text-sm font-bold ${highlight ? "text-blue-700" : "text-gray-900"
                        }`}
                >
                    {value}
                </span>
            </div>
        </motion.div>
    );



    const statusBadge = (status: any) => {
        console.log("Status:", status); // Log the status

        return (
            <motion.span
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.3 }}
                style={{
                    backgroundColor: status.bg_color,
                    color: status.text_color,
                }}
                className="px-2 py-0.5 rounded-full text-xs font-semibold"
            >
                {status.name}
            </motion.span>
        );
    };


    const section = (
        title: string,
        icon: React.ReactNode,
        content: React.ReactNode,
        bg?: string
    ) => (
        <motion.section
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className={`${bg || "bg-white"} shadow rounded-xl p-4 hover:shadow-xl transition-shadow duration-200`}
        >
            <h2 className="flex items-center gap-2 text-lg font-bold mb-2 text-gray-800">
                {icon} {title}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">{content}</div>
        </motion.section>
    );




    return (
        <Layout>
            <div className="max-w-full mx-auto p-4 space-y-6">
                <button
                    onClick={() => navigate(-1)}
                    className="flex items-center gap-2 text-blue-600 hover:text-blue-800 text-sm font-medium transition"
                >
                    <ArrowLeft size={16} /> Back
                </button>

                {/* <h1 className="text-2xl font-bold text-gray-800">Pet Service Order Details</h1> */}

                {section(
                    "Order Information",
                    <ListChecks size={18} />,
                    <>
                        {infoItem(
                            <Tag size={16} />,
                            "Order ID",
                            <div className="flex items-center gap-2">
                                <span className="px-3 py-1 rounded-lg bg-gradient-to-r from-blue-500 to-indigo-600 
                     text-white text-base font-bold shadow-sm tracking-wide">
                                    {order?.additional_info?.order_id}
                                </span>
                                <button
                                    onClick={() => navigator.clipboard.writeText(order?.additional_info?.order_id || "")}
                                    className="text-xs px-2 py-1 rounded-md border border-blue-300 text-blue-600 
                 hover:bg-blue-50 transition"
                                >
                                    Copy
                                </button>
                            </div>
                        )}

                        {infoItem(<Clock size={16} />, "Status", statusBadge(order?.service_status))}
                        {infoItem(<DollarSign size={16} />, "Total", `₹${order?.total_amount}`)}
                        {infoItem(
                            <Clock size={16} />,
                            "Hours",
                            order?.hours ? `${Math.round(order.hours * 60)} mins` : "-"
                        )}
                        {infoItem(
                            <Calendar size={16} />,
                            "Scheduled",
                            order?.scheduled_at
                                ? new Date(order.scheduled_at).toLocaleString("en-GB", {
                                    day: "2-digit",
                                    month: "2-digit",
                                    year: "numeric",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                    hour12: true,
                                })
                                : "-"
                        )}
                    </>,
                    "bg-gray-50"
                )}


                {section(
                    "Service Provider Information",
                    <User size={18} />,
                    <>
                        {infoItem(<User size={16} />, "First Name", order?.service_provider?.user?.first_name)}
                        {infoItem(<User size={16} />, "Last Name", order?.service_provider?.user?.last_name)}
                        {infoItem(<Tag size={16} />, "Service Provider ID", order?.service_provider?.id)}
                        {infoItem(<Phone size={16} />, "Mobile", order?.service_provider?.user?.mobile)}
                        {infoItem(<User size={16} />, "Email", order?.service_provider?.user?.email)}
                        {infoItem(<User size={16} />, "Gender", order?.service_provider?.user?.gender)}

                    </>
                )}
                {section(
                    "Owner Information",
                    <User size={18} />,
                    <>
                        {infoItem(<User size={16} />, "First Name", order?.owner?.first_name)}
                        {infoItem(<User size={16} />, "Last Name", order?.owner?.last_name)}
                        {infoItem(<Tag size={16} />, "Owner ID", order?.owner?.id)}
                        {infoItem(<Phone size={16} />, "Mobile", order?.owner?.mobile)}
                        {infoItem(<User size={16} />, "Email", order?.owner?.email)}
                        {infoItem(<User size={16} />, "Gender", order?.owner?.gender)}
                        {infoItem(<MapPin size={16} />, "Address", formatAddress(addressDetails))}
                    </>
                )}


                {section(
                    "Pet Information",
                    <PawPrint size={18} />,
                    <>
                        {infoItem(<Tag size={16} />, "Pet ID", order?.pet?.id)}
                        {infoItem(<User size={16} />, "Name", order?.pet?.additional_info?.name)}
                        {infoItem(<Tag size={16} />, "Breed", order?.pet?.additional_info?.type)}
                        {infoItem(<Tag size={16} />, "Species", order?.pet?.additional_info?.species)}
                        {infoItem(<Tag size={16} />, "Gender", order?.pet?.additional_info?.gender)}
                        {infoItem(<Tag size={16} />, "Age", order?.pet?.additional_info?.age)}
                        {infoItem(<Tag size={16} />, "Color", order?.pet?.additional_info?.color)}
                        {infoItem(<Tag size={16} />, "Size", order?.pet?.additional_info?.size)}
                        {infoItem(<Tag size={16} />, "Weight", `${order?.pet?.additional_info?.weight} kg`)}
                    </>
                )}

                <div className="w-full col-span-full rounded-xl border border-indigo-200 bg-gradient-to-br from-indigo-50 to-white p-6 shadow-md">
                    {/* Title */}
                    <div className="flex items-center gap-2 mb-5 border-b pb-3">
                        <div className="p-2 rounded-lg bg-indigo-100 text-indigo-600">
                            <ReceiptIndianRupee size={18} />
                        </div>
                        <h2 className="text-lg font-bold text-gray-900">Payment Details</h2>
                    </div>

                    {order?.service_charge_code ? (
                        <div className="space-y-5">
                            {/* Header */}
                            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                                Service Charge Breakdown
                            </h3>

                            {/* Details Grid */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                {/* Service */}
                                <div className="p-4 rounded-lg border border-gray-200 bg-white shadow-sm hover:shadow-md transition">
                                    <div className="flex items-center gap-2 text-gray-500 text-xs">
                                        <Tag size={14} /> Service
                                    </div>
                                    <div className="mt-1 font-semibold text-gray-900">
                                        {order?.service_charge_code?.service_name || "N/A"}
                                    </div>
                                </div>

                                {/* Charge Code */}
                                <div className="p-4 rounded-lg border border-gray-200 bg-white shadow-sm hover:shadow-md transition">
                                    <div className="flex items-center gap-2 text-gray-500 text-xs">
                                        <Tag size={14} /> Charge Code
                                    </div>
                                    <div className="mt-1 font-semibold text-gray-900">
                                        {order?.service_charge_code?.charge_code || "N/A"}
                                    </div>
                                </div>


                                <div className="p-4 rounded-lg border border-gray-200 bg-white shadow-sm hover:shadow-md transition">
                                    <div className="flex items-center gap-2 text-gray-500 text-xs">
                                        <DollarSign size={14} /> Amount
                                    </div>
                                    <div className="mt-1 font-semibold text-gray-900">
                                        ₹{order?.service_charge_code?.amount ?? 0}
                                    </div>
                                </div>

                                {/* Duration */}
                                <div className="p-4 rounded-lg border border-gray-200 bg-white shadow-sm hover:shadow-md transition">
                                    <div className="flex items-center gap-2 text-gray-500 text-xs">
                                        <Clock size={14} /> Duration
                                    </div>
                                    <div className="mt-1 font-semibold text-gray-900">
                                        {order?.service_charge_code?.duration
                                            ? `${Math.round(Number(order.service_charge_code.duration) * 60)} mins`
                                            : "0 mins"}
                                    </div>
                                </div>


                                {/* Applied Tax Codes */}
                                {Array.isArray(order.service_charge_code?.additional_info?.tax_codes) &&
                                    order.service_charge_code.additional_info.tax_codes.length > 0 && (
                                        <div className="col-span-2">
                                            <h4 className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wide">
                                                Applied Tax Codes
                                            </h4>

                                            <div className="divide-y divide-gray-100 border rounded-md bg-white shadow-sm">
                                                {order.service_charge_code.additional_info.tax_codes.map((tax: any) => {
                                                    const baseAmount = Number(order?.service_charge_code?.amount ?? 0);
                                                    let taxAmount = 0;

                                                    if (tax.type === "percentage") {
                                                        taxAmount = (baseAmount * Number(tax.rate ?? 0)) / 100;
                                                    } else if (tax.type === "adhoc") {
                                                        taxAmount = Number(tax.rate ?? 0);
                                                    }

                                                    return (
                                                        <div
                                                            key={tax.id}
                                                            className="flex justify-between items-center px-4 py-2 text-sm"
                                                        >
                                                            <span className="text-gray-600">
                                                                {tax.name} ({tax.code})
                                                            </span>
                                                            <span className="text-gray-900 font-medium">
                                                                {tax.type === "percentage"
                                                                    ? `${tax.rate}% → ₹${taxAmount.toFixed(2)}`
                                                                    : `₹${taxAmount.toFixed(2)}`}
                                                            </span>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}


                                {/* Charge Total - Highlighted */}
                                <div className="col-span-2 p-5 rounded-lg border border-green-200 bg-green-50 shadow-sm">
                                    <div className="flex items-center gap-2 text-gray-500 text-xs">
                                        <DollarSign size={14} /> Charge Total
                                    </div>
                                    <div className="mt-2 font-extrabold text-green-700 text-2xl">
                                        ₹{order.service_charge_code.total_amount ?? 0}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <p className="text-sm text-gray-500">No charge code details available.</p>
                    )}
                </div>
                <div className="mb-6">
                    <h2 className="text-3xl font-black text-center mb-6 bg-gradient-to-r from-purple-600 via-pink-600 to-rose-600 bg-clip-text text-transparent">
                        Service Review
                    </h2>
                    <ReviewCard review={review} loadingReview={loadingReview} />
                </div>
            </div>
        </Layout>
    );
};

export default PetServiceOrderDetails;
